import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { Regime } from '../lib/supabase';

export function RegisterPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    ruc: '',
    regime: 'general' as Regime,
  });

  function update(field: string, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (form.ruc.length !== 11) {
      setError('El RUC debe tener 11 dígitos');
      return;
    }
    setLoading(true);
    setError('');
    const { error } = await signUp(form.email, form.password, form.fullName, {
      name: form.companyName,
      ruc: form.ruc,
      regime: form.regime,
    });
    if (error) {
      setError(error.message || 'Error al registrar. Intenta nuevamente.');
      setLoading(false);
    } else {
      navigate('/dashboard');
    }
  }

  const regimes = [
    { value: 'general', label: 'Régimen General', desc: '30 días vacaciones, gratificaciones completas, CTS completa', icon: 'business' },
    { value: 'pequeña_empresa', label: 'REMYPE - Pequeña Empresa', desc: '15 días vacaciones, ½ gratificación, CTS al 50%', icon: 'storefront' },
    { value: 'microempresa', label: 'REMYPE - Microempresa', desc: 'Sin gratificaciones, sin CTS obligatoria', icon: 'cottage' },
  ];

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-6 py-12">
      <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />

      <div className="w-full max-w-lg z-10 animate-fade-in">
        <div className="mb-8 text-center">
          <h1 className="font-headline font-extrabold text-3xl tracking-tight text-on-surface mb-1">
            Payroll Atelier
          </h1>
          <p className="text-on-surface-variant text-sm">Registra tu empresa y empieza en minutos</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2].map(s => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                s === step ? 'bg-primary text-white' : s < step ? 'bg-green-500 text-white' : 'bg-surface-container-high text-on-surface-variant'
              }`}>
                {s < step ? <span className="material-symbols-outlined text-[16px]">check</span> : s}
              </div>
              {s < 2 && <div className={`h-px w-16 transition-all ${s < step ? 'bg-primary' : 'bg-outline-variant/30'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-surface-container-lowest rounded-xl p-8 shadow-[0_20px_40px_rgba(25,28,30,0.08)] border border-outline-variant/10">
          <form onSubmit={handleSubmit}>
            {/* ── Step 1: Personal & Account ── */}
            {step === 1 && (
              <div className="space-y-5 animate-fade-in">
                <h2 className="font-headline font-bold text-xl text-on-surface mb-4">Datos de la cuenta</h2>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Nombre completo</label>
                  <input className="input-base" value={form.fullName} onChange={e => update('fullName', e.target.value)} placeholder="Tu nombre completo" required />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Email</label>
                  <input className="input-base" type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="admin@empresa.com" required />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Contraseña</label>
                  <input className="input-base" type="password" value={form.password} onChange={e => update('password', e.target.value)} placeholder="Mínimo 8 caracteres" required minLength={8} />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Confirmar contraseña</label>
                  <input className="input-base" type="password" value={form.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} placeholder="Repite tu contraseña" required />
                </div>

                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!form.fullName || !form.email || !form.password || form.password !== form.confirmPassword}
                  className="w-full btn-primary text-white font-bold text-sm uppercase tracking-wider py-3.5 rounded-lg shadow-lg hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40 mt-2"
                >
                  Continuar →
                </button>
              </div>
            )}

            {/* ── Step 2: Company ── */}
            {step === 2 && (
              <div className="space-y-5 animate-fade-in">
                <h2 className="font-headline font-bold text-xl text-on-surface mb-4">Datos de la empresa</h2>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Razón social</label>
                  <input className="input-base" value={form.companyName} onChange={e => update('companyName', e.target.value)} placeholder="Empresa S.A.C." required />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">RUC (11 dígitos)</label>
                  <input className="input-base" value={form.ruc} onChange={e => update('ruc', e.target.value.replace(/\D/g, '').slice(0, 11))} placeholder="20123456789" required pattern="\d{11}" />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Régimen laboral</label>
                  <p className="text-xs text-on-surface-variant">Este parámetro define todos los beneficios y descuentos de tu planilla</p>
                  <div className="space-y-2 mt-2">
                    {regimes.map(r => (
                      <label key={r.value} className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer border-2 transition-all ${
                        form.regime === r.value ? 'border-primary bg-primary/5' : 'border-outline-variant/20 hover:border-primary/30 hover:bg-surface-container-low'
                      }`}>
                        <input
                          type="radio"
                          name="regime"
                          value={r.value}
                          checked={form.regime === r.value}
                          onChange={e => update('regime', e.target.value)}
                          className="mt-0.5 accent-primary"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-primary text-[18px]">{r.icon}</span>
                            <p className="text-sm font-bold text-on-surface">{r.label}</p>
                          </div>
                          <p className="text-xs text-on-surface-variant mt-0.5">{r.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-error-container rounded-lg">
                    <span className="material-symbols-outlined text-error text-[18px]">error</span>
                    <p className="text-xs text-on-error-container">{error}</p>
                  </div>
                )}

                <div className="flex gap-3 mt-2">
                  <button type="button" onClick={() => setStep(1)} className="flex-1 px-4 py-3.5 rounded-lg border border-outline-variant/30 text-sm font-bold text-on-surface-variant hover:bg-surface-container-low transition-all">
                    ← Atrás
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !form.companyName || !form.ruc}
                    className="flex-1 btn-primary text-white font-bold text-sm uppercase tracking-wider py-3.5 rounded-lg shadow-lg hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Registrando...
                      </span>
                    ) : 'Crear cuenta'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-on-surface-variant">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-primary font-bold hover:underline">Iniciar sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
